import React from 'react'
import { cn } from '../helpers/utils.js'
import { Slot } from '@radix-ui/react-slot'
import type { Mode, SpaceY, SpaceTop, SpaceBottom } from '../types/props.js'

type ContainerElement = React.ElementRef<'div'>
type ContainerProps = {
  asChild?: boolean
  children?: React.ReactNode
  className?: string
  childClassName?: string
  spaceY?: SpaceY
  spaceTop?: SpaceTop
  spaceBottom?: SpaceBottom
  mode?: Mode
} & React.ComponentPropsWithoutRef<'div'>

const Fragment = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>
}

const Section = React.forwardRef<ContainerElement, ContainerProps>(({ ...props }, forwardedRef) => {
  const {
    asChild,
    children,
    className,
    childClassName,
    spaceY = 'none',
    spaceTop = 'none',
    spaceBottom = 'none',
    ...containerProps
  } = props

  const Comp = asChild ? Slot : 'div'

  const spaceY_map: any = {
    default: 'tablet:my-[80px] laptop:my-[96px] desktop:my-[112px] lg-desktop:my-[128px] my-[64px]',
    none: '',
  }

  const spaceTop_map: any = {
    default: 'tablet:pt-[80px] laptop:pt-[96px] desktop:pt-[112px] lg-desktop:pt-[128px] pt-[64px]',
    none: '',
  }

  const spaceBottom_map: any = {
    default: 'tablet:pb-[80px] laptop:pb-[96px] desktop:pb-[112px] lg-desktop:pb-[128px] pb-[64px]',
    none: '',
  }

  return (
    <Comp {...containerProps} ref={forwardedRef} className={cn(className, 'ipc_container', 'relative z-10 w-full')}>
      <div
        className={cn(
          'lg-desktop:max-w-full mx-auto size-full',
          'tablet:px-8 laptop:px-16 desktop:px-16 lg-desktop:px-[calc(50%-832px)] px-4',
          childClassName,
          spaceY_map?.[spaceY] || spaceY,
          spaceTop_map?.[spaceTop] || spaceTop,
          spaceBottom_map?.[spaceBottom] || spaceBottom
        )}
      >
        {children}
      </div>
    </Comp>
  )
})

Section.displayName = 'Section'

/**
 * Container - 容器
 *
 * @description 页面布局容器组件，提供响应式间距和最大宽度限制，支持不同模式和间距配置
 */
const Container = React.forwardRef<ContainerElement, ContainerProps>(({ ...props }, forwardedRef) => {
  const { children, mode = 'section' } = props

  const componentsMap: {
    [P in Mode]: React.ComponentType<any> | string
  } = {
    section: Section,
    fragment: Fragment,
  }

  const Component = componentsMap![mode!]

  return (
    <Component {...props} ref={forwardedRef}>
      {children}
    </Component>
  )
})

Container.displayName = 'Container'

export { Container }
export type { ContainerProps }
